<div class="vault-game" [class.is-immersive]="isFullscreen" *ngIf="round">

  <!-- Round progress -->
  <div class="round-dots">
    <span class="dot" *ngFor="let d of roundDots; let i = index"
          [class.done]="solvedFlags[i]" [class.current]="i === currentRound"></span>
  </div>

  <!-- Single play panel: prompt header + safe-cracking board on one surface -->
  <div class="play-panel">

    <!-- Prompt header — the percent question. The math expression is an isolated
         LTR run so "= ?" sits at the visual end even in Arabic. The turn badge
         shares this row on the trailing edge (§3.7). Mirrors in RTL. -->
    <div class="panel-head">
      <div class="head-prompt">
        <span class="prompt-icon" aria-hidden="true">🔐</span>
        <span class="prompt-math" dir="ltr">{{ promptText }}</span>
        <span class="prompt-chip" *ngIf="round.bridgeChip">
          {{ currLang === 'ar' ? round.bridgeChip.ar : round.bridgeChip.en }}
        </span>
      </div>

      <div class="turn-chip" *ngIf="student">
        <app-student-avatar [student]="student" [size]="46" [active]="true"></app-student-avatar>
        <div class="turn-text">
          <span class="turn-name">{{ studentName }}</span>
          <span class="turn-label">{{ currLang === 'ar' ? 'دورك!' : "it's your turn" }}</span>
        </div>
      </div>
    </div>

    <!-- Interactive board -->
    <div class="board-area">
      <app-vault-cracker-stage
        [round]="round"
        [roundKey]="currentRound"
        [hintVisible]="hintVisible"
        [interactive]="!resolving && !showTurnCard"
        [currLang]="currLang"
        (solved)="onSolved($event)"
        (missed)="onMissed($event)">
      </app-vault-cracker-stage>
    </div>

    <!-- Scaffolded content hint (appears after the struggle beat; never the answer) -->
    <div class="hint-panel" [class.is-visible]="hintVisible && !resolving" aria-live="polite">
      <span class="hint-bulb" aria-hidden="true">💡</span>
      <span class="hint-text">{{ currLang === 'ar' ? hintText.ar : hintText.en }}</span>
    </div>

    <!-- Kind coach — Rocco the raccoon vault-keeper (theme §3.2). PERSISTENT
         element toggled purely by opacity (never created/destroyed → no
         flicker); a repeat miss swaps the copy in place. pointer-events:none. -->
    <div class="rocco-encourage" [class.is-visible]="showRocco" aria-live="polite">
      <svg class="rocco-portrait" viewBox="0 0 120 140" aria-hidden="true">
        <!-- Striped tail curling behind -->
        <path d="M96 118 C120 112 118 78 100 74 C112 82 108 104 92 104 Z" fill="#8B94A3" stroke="#4C5563" stroke-width="1.5"/>
        <path d="M104 92 q8 -2 8 -10" stroke="#2E3540" stroke-width="4" fill="none" stroke-linecap="round"/>
        <path d="M100 104 q10 -1 12 -9" stroke="#2E3540" stroke-width="4" fill="none" stroke-linecap="round"/>
        <!-- Body / vest -->
        <path d="M32 138 C30 112 44 100 60 100 C76 100 90 112 88 138 Z" fill="#5B6675" stroke="#3A424E" stroke-width="1.5"/>
        <rect x="52" y="104" width="16" height="30" rx="6" fill="#3A424E"/>
        <!-- Stethoscope (safe-listener) -->
        <path d="M46 108 C40 122 58 128 60 116" fill="none" stroke="#1D2939" stroke-width="3" stroke-linecap="round"/>
        <circle cx="60" cy="118" r="6" fill="#C0C8D2" stroke="#5B6675" stroke-width="1.5"/>
        <!-- Fingerless gloves / paws -->
        <circle cx="38" cy="120" r="8" fill="#3A424E"/>
        <circle cx="82" cy="120" r="8" fill="#3A424E"/>
        <!-- Head -->
        <ellipse cx="60" cy="66" rx="34" ry="30" fill="#9AA6B5" stroke="#4C5563" stroke-width="1.5"/>
        <!-- Ears -->
        <path d="M30 44 L24 24 L46 38 Z" fill="#9AA6B5" stroke="#4C5563" stroke-width="1.5"/>
        <path d="M90 44 L96 24 L74 38 Z" fill="#9AA6B5" stroke="#4C5563" stroke-width="1.5"/>
        <path d="M32 42 L29 30 L41 38 Z" fill="#5B6675"/>
        <path d="M88 42 L91 30 L79 38 Z" fill="#5B6675"/>
        <!-- Bandit eye-mask -->
        <path d="M26 62 C40 50 80 50 94 62 C86 74 74 76 60 76 C46 76 34 74 26 62 Z" fill="#2E3540"/>
        <!-- Muzzle -->
        <ellipse cx="60" cy="80" rx="20" ry="14" fill="#EDF1F6"/>
        <!-- Eyes -->
        <circle cx="47" cy="63" r="6.5" fill="#FFFFFF"/>
        <circle cx="73" cy="63" r="6.5" fill="#FFFFFF"/>
        <circle cx="48" cy="64" r="3.6" fill="#1D2939"/>
        <circle cx="72" cy="64" r="3.6" fill="#1D2939"/>
        <circle cx="49" cy="62.5" r="1.2" fill="#FFFFFF"/>
        <circle cx="73" cy="62.5" r="1.2" fill="#FFFFFF"/>
        <!-- Nose + smile -->
        <ellipse cx="60" cy="74" rx="5" ry="3.6" fill="#1D2939"/>
        <path d="M52 82 Q60 90 68 82" fill="none" stroke="#5B6675" stroke-width="2.5" stroke-linecap="round"/>
        <!-- Whiskers -->
        <line x1="80" y1="78" x2="96" y2="74" stroke="#B7C0CC" stroke-width="1.5"/>
        <line x1="80" y1="82" x2="96" y2="84" stroke="#B7C0CC" stroke-width="1.5"/>
        <line x1="40" y1="78" x2="24" y2="74" stroke="#B7C0CC" stroke-width="1.5"/>
        <line x1="40" y1="82" x2="24" y2="84" stroke="#B7C0CC" stroke-width="1.5"/>
      </svg>
      <div class="rocco-bubble">
        <span class="rocco-line">{{ currLang === 'ar' ? roccoLine.ar : roccoLine.en }}</span>
      </div>
    </div>
  </div>

  <!-- Controls -->
  <div class="controls">
    <button class="ctrl-btn set-btn" [disabled]="resolving || showTurnCard" (click)="onSet()">
      <span aria-hidden="true">🔓</span>
      {{ currLang === 'ar' ? 'اضبط' : 'SET' }}
    </button>

    <button class="ctrl-btn skip-btn" [disabled]="resolving" (click)="skip()">
      {{ currLang === 'ar' ? 'تخطّي' : 'Skip' }}
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>

  <!-- Success -->
  <div class="success-overlay" *ngIf="showSuccess">
    <div class="success-card">
      <div class="success-icon" aria-hidden="true">💎</div>
      <div class="success-title">{{ currLang === 'ar' ? 'الخزنة مفتوحة!' : 'Vault cracked!' }}</div>
      <div class="success-xp" *ngIf="lastXp > 0">+{{ lastXp }} XP</div>
    </div>
  </div>

  <!-- Turn-intro card -->
  <div class="turn-card-overlay" *ngIf="showTurnCard" (click)="dismissTurnCard()">
    <div class="turn-card">
      <div class="tc-emoji" aria-hidden="true">🔐</div>
      <app-student-avatar *ngIf="student" [student]="student" [size]="96" [active]="true"></app-student-avatar>
      <div class="tc-name">{{ studentName }}</div>
      <div class="tc-label">{{ currLang === 'ar' ? 'دورك!' : "It's your turn" }}</div>
      <div class="tc-tap">{{ currLang === 'ar' ? 'انقر للبدء' : 'tap to start' }}</div>
    </div>
  </div>

  <!-- Own fullscreen button — matches the "Select Game" chrome button (§3.7). -->
  <button class="fs-btn" type="button" (click)="toggleFullscreen()"
          [attr.aria-label]="isFullscreen
            ? (currLang === 'ar' ? 'تصغير' : 'Minimize')
            : (currLang === 'ar' ? 'ملء الشاشة' : 'Fullscreen')"
          [title]="isFullscreen
            ? (currLang === 'ar' ? 'تصغير' : 'Minimize')
            : (currLang === 'ar' ? 'ملء الشاشة' : 'Fullscreen')">
    <svg *ngIf="isFullscreen" width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M8 3v5H3"/><path d="M21 8h-5V3"/><path d="M16 21v-5h5"/><path d="M3 16h5v5"/>
    </svg>
    <svg *ngIf="!isFullscreen" width="18" height="18" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 8V3h5"/><path d="M16 3h5v5"/><path d="M21 16v5h-5"/><path d="M8 21H3v-5"/>
    </svg>
    <span class="fs-label">{{ isFullscreen
      ? (currLang === 'ar' ? 'تصغير' : 'Minimize')
      : (currLang === 'ar' ? 'ملء الشاشة' : 'Fullscreen') }}</span>
  </button>
</div>
